import { MutableRefObject, useEffect, useRef } from 'react'

import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

export const useTrackIsElementVisible = (getTrackProperties: () => any, cb?: any) => {
  const ref: MutableRefObject<HTMLDivElement | null> = useRef(null)
  const seenRef = useRef(false)
  useEffect(() => {
    const onScroll = function () {
      const el = ref.current
      if (el && isInViewport(el) && !seenRef.current) {
        seenRef.current = true
        if (getTrackProperties()) trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, getTrackProperties())
        if (cb) {
          cb()
        }
        document.removeEventListener('scroll', onScroll)
      }
    }
    document.addEventListener('scroll', onScroll)
    return () => {
      document.removeEventListener('scroll', onScroll)
    }
  }, [getTrackProperties, cb])
  return { ref }
}

const DELAY_MS = 1000
const DATA_REVIEW_INDEX = 'data-review-index'

export interface UsePerReviewVisibilityOptions {
  reviewWrapperRefs: MutableRefObject<(HTMLDivElement | null)[]>
  reviewCount: number
  pageNumber: number
  onReviewVisible?: (review_count: number, page_number: number) => void
}

// Fires when the user scrolls and the bottom-most fully visible review changes (after 1s debounce).
export function usePerReviewVisibility({
  reviewWrapperRefs,
  reviewCount,
  pageNumber,
  onReviewVisible,
}: UsePerReviewVisibilityOptions) {
  const firedPage = useRef<number | null>(null)
  const visible = useRef<Set<number>>(new Set())
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firedBottom = useRef<Set<number>>(new Set())

  useEffect(() => {
    if (!onReviewVisible || reviewCount === 0) return

    const clearTimer = () => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = null
    }

    if (pageNumber > 1) {
      if (firedPage.current === pageNumber) return
      const id = setTimeout(() => {
        firedPage.current = pageNumber
        onReviewVisible(reviewCount, pageNumber)
      }, DELAY_MS)
      return () => {
        clearTimeout(id)
      }
    }
    visible.current.clear()
    firedBottom.current.clear()
    clearTimer()

    const schedule = () => {
      clearTimer()
      if (visible.current.size === 0) return
      timer.current = setTimeout(() => {
        timer.current = null
        if (visible.current.size === 0) return
        const bottom = Math.max(...visible.current)
        if (firedBottom.current.has(bottom)) return
        firedBottom.current.add(bottom)
        onReviewVisible(bottom + 1, pageNumber)
      }, DELAY_MS)
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          const i = parseInt((e.target as HTMLElement).getAttribute(DATA_REVIEW_INDEX) ?? '', 10)
          if (!Number.isNaN(i)) e.isIntersecting ? visible.current.add(i) : visible.current.delete(i)
        }
        schedule()
      },
      { threshold: 1 },
    )

    const deferId = setTimeout(() => {
      const refs = reviewWrapperRefs.current
      for (let i = 0; i < reviewCount; i++) {
        const el = refs[i]
        if (el) observer.observe(el)
      }
    }, 0)

    return () => {
      clearTimeout(deferId)
      clearTimer()
      visible.current.clear()
      observer.disconnect()
    }
  }, [reviewCount, pageNumber, onReviewVisible])
}

function isInViewport(el: HTMLDivElement) {
  if (window.getComputedStyle(el).visibility === 'hidden' || window.getComputedStyle(el).display === 'none') {
    return false
  }
  const rect = el.getBoundingClientRect()
  if (rect.width === 0 || rect.height === 0) {
    return
  }
  const windowHeight = window.innerHeight || document.documentElement.clientHeight

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.top + windowHeight / 2 <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left + 200 <= (window.innerWidth || document.documentElement.clientWidth)
  )
}
